import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes as colType, SmallSpinner, baseEvent, pitchResult, rvERA, yesNo, columnTypes } from 'yankees-react-utils';
import { useOktaQuery, PlayerReport } from '../../../../data';
import VideoPlayerButton from '../../../../components/VideoPlayerButton';

const pitchLogQuery = async (accessToken, playerid, gameid, pitchtype, fxsubtype) => {
    const ptbfxDrillDownPitchesParams = { playerid, gameid, pitchtype, fxsubtype };
    const ptbfxDrillDownPitchesPromise = PlayerReport.ptbfxdrilldownpitches(accessToken, ptbfxDrillDownPitchesParams);
    const pitches = await ptbfxDrillDownPitchesPromise;
    return pitches;
};

const PitchLog = ({ className, playerid, gameid, pitchtype, fxsubtype, isInBats, useSubType }) => {
    const { t: translate } = useTranslation();
    const { data, status } = useOktaQuery(['pitchlog', { playerid, gameid, pitchtype, fxsubtype }], (accessToken) => pitchLogQuery(accessToken, playerid, gameid, pitchtype, useSubType === true ? fxsubtype : null));
    if (status === 'loading') {
        return <div><SmallSpinner />{translate('common.loading')}...</div>;
    }
    const rows = data.pitches.map((p) => {
        const videoparams = { pitcherid: playerid, gameid: p.gameid, eventseq: p.eventseq, pitchseq: p.pitchseq };
        const videoplaylist = isInBats ? (<VideoPlayerButton {...videoparams} />) : '--';
        return { ...p, videoplaylist };
    });
    const columns = [
        { id: 'videoplaylist', title: null, type: colType.NODE },
        { id: 'pitchnumber', title: translate('sections.reports.pitcher.pitch-number') },
        { id: 'inn', title: `${translate('baseball.abbr-inning')}.`, transform: (row) => `${row.istop ? 'T' : 'B'}${row.inning}`, type: colType.TEXT },
        { id: 'batter', title: translate('baseball.batter'), transform: (row) => `${row.batter_lastname}, ${row.batter_usesname.charAt(0)}`, type: colType.TEXT },
        { id: 'outs', title: translate('baseball.abbr-out') },
        { id: 'count', title: translate('baseball.abbr-count'), transform: (row) => `${row.balls}-${row.strikes}`, type: colType.TEXT },
        { id: 'result', title: translate('sections.reports.pitcher.result'), transform: (row) => (row.islastpitch ? baseEvent(row.baseevent, undefined, undefined, translate) : pitchResult(row.pitchresult, true, translate)), type: colType.TEXT },
        { id: 'initspeed54', title: translate('sections.reports.pitcher.abbr-velo'), precision: 1 },
        { id: 'breakx54', title: translate('sections.reports.pitcher.horiz-break'), precision: 1 },
        { id: 'breakz54', title: translate('sections.reports.pitcher.vert-break'), precision: 1 },
        { id: 'spinrate', title: translate('sections.reports.pitcher.spin-rate') },
        { id: 'spindirectionclock', title: translate('sections.reports.pitcher.spin-dir'), type: columnTypes.TEXT, headerClass: 'text-center', cellClass: 'text-center' },
        { id: 'gyrodegree', title: translate('sections.reports.pitcher.gyro-degree'), precision: 1 },
        { id: 'initposx', title: translate('sections.reports.pitcher.rel-side'), precision: 1 },
        { id: 'initposz', title: translate('sections.reports.pitcher.rel-height'), precision: 1 },
        { id: 'extension', title: translate('sections.reports.pitcher.abbr-extension'), precision: 2 },
        { id: 'stk', title: translate('sections.reports.pitcher.abbr-strike'), transform: (row) => yesNo(row.stk, false, translate), type: colType.TEXT },
        { id: 'closepitch', title: translate('sections.reports.pitcher.close-pitch'), transform: (row) => yesNo(row.closepitch === 1, false, translate), type: colType.TEXT },
        { id: 'wastepitch', title: translate('sections.reports.pitcher.waste-pitch'), transform: (row) => yesNo((row.wastepitch === 1 || row.noncomppitches === 1), false, translate), type: colType.TEXT },
        { id: 'ms', title: translate('sections.reports.pitcher.abbr-miss'), transform: (row) => yesNo(row.ms === 1 && row.sw === 1, false, translate), type: colType.TEXT },
        { id: 'calledk', title: translate('sections.reports.pitcher.called-k'), transform: (row) => yesNo(row.tkstk === 1, false, translate), type: colType.TEXT },
        { id: 'chase', title: translate('sections.reports.pitcher.chase'), transform: (row) => yesNo(row.ch === 1 && row.outz === 1, false, translate), type: colType.TEXT },
        { id: 'putaway', title: translate('sections.reports.pitcher.put-away'), transform: (row) => yesNo(row.so && row.twostrikepitch === 1, false, translate), type: colType.TEXT },
        { id: 'gb', title: translate('baseball.pitchresult.abbr-groundball'), transform: (row) => yesNo(row.gb === 1, false, translate), type: colType.TEXT },
        { id: 'hitvelocity', title: translate('sections.reports.pitcher.exit-velo'), precision: 1 },
        { id: 'wellhit', title: translate('sections.reports.pitcher.well-hit'), transform: (row) => yesNo(row.wellhit_exctm95 === 1 || row.wellhit === 1, false, translate), type: colType.TEXT },
        { id: 'pfxera', title: translate('sections.reports.pitcher.pfx-era'), transform: (row) => rvERA(row.rvpitchfx), precision: 2 },
        { id: 'locationera', title: translate('sections.reports.pitcher.location-era'), transform: (row) => rvERA(row.rvloc), precision: 2 },
        { id: 'stuffera', title: translate('sections.reports.pitcher.stuff-era'), transform: (row) => rvERA(row.rvstuff), precision: 2 },
        { id: 'breakera', title: translate('sections.reports.pitcher.break-era'), transform: (row) => rvERA(row.rvbreak), precision: 2 },
    ];

    return (
        <DataTable
            className={className}
            sortable={false}
            columns={columns}
            rows={rows}
            striped
        />
    );
};

PitchLog.propTypes = {
    className: PropTypes.string,
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
    pitchtype: PropTypes.number.isRequired,
    fxsubtype: PropTypes.number,
    isInBats: PropTypes.bool.isRequired,
    useSubType: PropTypes.bool,
};

PitchLog.defaultProps = {
    className: undefined,
    fxsubtype: null,
    useSubType: false,
};

export default PitchLog;
